import { ReactNode, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useTheme } from '@mui/material';

import { SidePanelContext } from './context';
import useSidePanel from './useSidePanel';

interface SidePanelProps {
  /** Stable identifier; namespaces the persisted width in localStorage. */
  id: string;
  open: boolean;
  /** Width (px) used on first open, before any cached width exists. */
  defaultWidth?: number;
  /** When true, shows the drag pill and lets the user resize (width persists per id). */
  resizable?: boolean;
  children?: ReactNode;
}

/**
 * Declarative entry point for a page's right-hand details panel. Renders
 * nothing in place — its children are portaled into the slot owned by the
 * SidePanelProvider (a pushed rounded panel on lg+, an overlay drawer below).
 */
const SidePanel = ({
  id,
  open,
  defaultWidth = 400,
  resizable = false,
  children,
}: SidePanelProps) => {
  useSidePanel({ id, open, defaultWidth, resizable });

  const context = useContext(SidePanelContext);
  const theme = useTheme();

  // Hold the last content through the close transition so it stays visible
  // while the panel slides out — the page clears its own state on close.
  const [displayed, setDisplayed] = useState<ReactNode>(null);
  useEffect(() => {
    if (open) {
      setDisplayed(children);
      return undefined;
    }
    const timer = setTimeout(() => setDisplayed(null), theme.transitions.duration.standard);
    return () => clearTimeout(timer);
  }, [open, children, theme.transitions.duration.standard]);

  const content = open ? children : displayed;

  if (!context?.slotElement || !content) {
    return null;
  }

  return createPortal(content, context.slotElement);
};

export default SidePanel;
